import { ReactNode, ReactElement } from 'react';

import { useFlags } from 'launchdarkly-react-client-sdk';

import RouteGuard from 'domains/core/components/RouteGuard';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import { AccessCentreRouteAccessMode } from 'models/enums';

import type { PermissionKey } from 'domains/accessCentre/types';

export type AccessCentreRouteGuardProps = {
    children: ReactNode;
    accessMode: AccessCentreRouteAccessMode;
    /** Single permission key (omit if using `permissionsAnyOf`). */
    permission?: PermissionKey;
    /** If set, access granted when the user has **any** of these keys (when gated). */
    permissionsAnyOf?: PermissionKey[];
    accessDeniedComponent?: ReactNode;
};

/**
 * Access Centre + LaunchDarkly route guard: shared loading logic, configurable access rule and permission key(s).
 */
export const AccessCentreRouteGuard = ({
    children,
    accessMode,
    permission,
    permissionsAnyOf,
    accessDeniedComponent,
}: AccessCentreRouteGuardProps): ReactElement | null => {
    const { accessCenter: accessCenterFlag } = useFlags();
    const { hasAccessCentrePermission, isAccessCentrePermissionsLoading } = useAccessCentrePermissions();

    const isLoading = accessCenterFlag === undefined || (accessCenterFlag === true && isAccessCentrePermissionsLoading);

    const permissionGranted = permissionsAnyOf?.length
        ? permissionsAnyOf.some((k) => hasAccessCentrePermission(k))
        : permission
        ? hasAccessCentrePermission(permission)
        : false;

    const hasRouteAccess =
        accessMode === AccessCentreRouteAccessMode.RequireFeatureAndPermission
            ? !!accessCenterFlag && permissionGranted
            : accessCenterFlag !== true || permissionGranted;

    return (
        <RouteGuard hasPermission={hasRouteAccess} isLoading={isLoading} accessDeniedComponent={accessDeniedComponent}>
            {children}
        </RouteGuard>
    );
};
